import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError, Status } from '../api/client'
import './RtoRecord.css'

const STATUS_LABEL: Record<number, string> = {
  [Status.Known]: 'Recorded',
  [Status.NoExpiry]: 'No expiry',
  [Status.NotApplicable]: 'Not applicable',
  [Status.Unknown]: 'Not recorded',
}

const PARSE_STATUS: Record<number, string> = {
  1: 'Parsed',
  2: 'Partially parsed',
  3: 'Unparseable',
}

function date(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function stamp(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * One field of the record.
 *
 * A framed pair rather than a table row. These bands are not tables — there are no
 * columns, only a name and an answer — and rendering them as one forced a header
 * row onto each ("Field | What the RTO holds", three times over) to say what the
 * shape already says. Worse, every table sized its own label column, so the tinted
 * strip landed in a different place in each and the eye had nothing to follow.
 *
 * Label above value, small caps over full ink: the two can no longer be confused
 * for one another, and nothing has to be repeated to explain it.
 */
function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || <span className="rto__none">not stated</span>}</dd>
    </div>
  )
}

/**
 * Everything the registry said about one vehicle.
 *
 * A page rather than a modal, and reached from the vehicle it belongs to — it is a
 * document you read, and this codebase routes full pages rather than overlays.
 *
 * Three things are reachable here and nowhere else:
 *
 *   * The identity fields staged from VAHAN. Owner, RTO office and the exact
 *     registration date are never written to dbo.Vehicle at all, so this is the
 *     only place they exist for a reader.
 *   * The provenance of the call — what it cost, how long it took, where the
 *     payload is archived. A compliance date that cannot be traced to a response is
 *     a date somebody has to take on trust.
 *   * The unmapped fields, and the raw payload. Every key in the response no
 *     mapping claimed: the feedback loop that makes a field we paid for and walked
 *     past visible instead of invisible.
 *
 * ⚠️ Behind the "RegistryAdmin" policy, which TODAY means nothing more than "signed
 * in" — this system has no roles yet. This page carries owner names and financer
 * details, so it is not restricted until that policy actually restricts something.
 */
export function RtoRecord() {
  const { registration = '' } = useParams()
  const navigate = useNavigate()
  const [showRaw, setShowRaw] = useState(false)

  const lookup = useQuery({
    queryKey: ['vehicle-lookup', registration],
    queryFn: () => api.findByRegistration(registration),
  })

  const vehicleId = lookup.data?.vehicleId

  const vehicle = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => api.getVehicle(vehicleId!),
    enabled: vehicleId != null,
  })

  const snapshot = useQuery({
    queryKey: ['registry-snapshot', vehicleId],
    queryFn: () => api.getRegistrySnapshot(vehicleId!),
    enabled: vehicleId != null,
  })

  const raw = useQuery({
    queryKey: ['registry-raw', vehicleId],
    queryFn: () => api.getRegistryRawResponse(vehicleId!),
    // Only fetched once somebody asks: it is the whole payload, and nobody needs it
    // to read the tables above.
    enabled: showRaw && Boolean(vehicleId),
  })

  const unmapped = useMemo(() => {
    const json = snapshot.data?.unmappedFieldsJson
    if (!json) return []
    try {
      return Object.entries(JSON.parse(json) as Record<string, unknown>)
    } catch {
      // A payload we could not re-read is worth saying so about, not worth crashing
      // the page for.
      return []
    }
  }, [snapshot.data])

  const back = (
    <button type="button" className="rto__back" onClick={() => navigate(-1)}>
      ← Back
    </button>
  )

  if (lookup.isError) {
    const notFound = lookup.error instanceof ApiError && lookup.error.status === 404
    return (
      <div className="rto">
        {back}
        <p className="rto__empty">
          {notFound ? `No vehicle called ${registration}.` : 'Could not load this vehicle.'}
        </p>
      </div>
    )
  }

  if (snapshot.isPending || lookup.isPending) {
    return (
      <div className="rto">
        {back}
        <p className="rto__empty">Loading…</p>
      </div>
    )
  }

  const s = snapshot.data

  return (
    <div className="rto">
      <div className="rto__head">
        {back}
        <div>
          <h2 className="mono">{registration}</h2>
          <p className="rto__sub">
            What the RTO holds
            {s?.registryAsOfDate && <> · registry current to {date(s.registryAsOfDate)}</>}
          </p>
        </div>
        <Link className="rto__open" to={`/vehicles/${registration}`}>
          Open the register record
        </Link>
      </div>

      {/*
        Nothing held is a different state from nothing returned, and only one of
        them is worth acting on. Saying so beats an empty table, which reads as
        "the RTO has no record of this vehicle" — a fabricated fact.
      */}
      {!s ? (
        <p className="rto__empty">
          No RTO data is held for this vehicle. It has never been fetched from the
          registry — which is not the same as the registry having nothing to say.
        </p>
      ) : (
        <>
          <div className="rto__grid">
            <section>
              <h4>Identity</h4>
              <dl className="rto__facts">
                <Row label="Owner" value={s.ownerName} />
                <Row label="RTO office" value={s.rtoName} />
                <Row label="Registered" value={date(s.registrationDate)} />
                <Row label="Make" value={s.make} />
                <Row label="Model" value={s.model} />
                <Row label="Chassis" value={s.chassisNumber} />
                <Row label="Engine" value={s.engineNumber} />
              </dl>
            </section>

            <section>
              <h4>Classification</h4>
              <dl className="rto__facts">
                <div>
                  <dt>RC status</dt>
                  <dd
                    className={
                      s.rcStatus && s.rcStatus.toUpperCase() !== 'ACTIVE'
                        ? 'rto__alert'
                        : undefined
                    }
                  >
                    {s.rcStatus || <span className="rto__none">not stated</span>}
                  </dd>
                </div>
                <Row label="Class" value={s.vehicleClass} />
                <Row label="Fuel" value={s.fuelType} />
                <Row label="Emission norms" value={s.emissionNorms} />
              </dl>

              <h4>Finance</h4>
              <dl className="rto__facts">
                <div>
                  <dt>Financed</dt>
                  <dd>
                    {s.isFinanced === true
                      ? 'Yes'
                      : s.isFinanced === false
                        ? 'No'
                        : // Three states, not two: the registry never said.
                          <span className="rto__none">not stated</span>}
                  </dd>
                </div>
                <Row label="Financer" value={s.financer} />
              </dl>
            </section>

            {/*
              The comparison band. The register and the registry side by side, with
              disagreements marked — which is the entire reason this module exists,
              and the only screen that shows both at once.
            */}
            {vehicle.data && (
              <section className="rto__wide">
                <h4>Against the register</h4>
                <table className="rto__tbl rto__tbl--cmp rto__tbl--vs">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>The register</th>
                      <th>The RTO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <Compare label="Make" ours={vehicle.data.make} theirs={s.make} />
                    <Compare label="Model" ours={vehicle.data.model} theirs={s.model} />
                    <Compare
                      label="Chassis"
                      ours={vehicle.data.chassisNumber}
                      theirs={s.chassisNumber}
                    />
                    <Compare
                      label="Engine"
                      ours={vehicle.data.engineNumber}
                      theirs={s.engineNumber}
                    />
                    <Compare
                      label="Registration year"
                      ours={vehicle.data.registrationYear?.toString()}
                      theirs={s.registrationDate?.slice(0, 4)}
                    />
                  </tbody>
                </table>
              </section>
            )}

            <section className="rto__wide">
              <h4>Obligations, as the registry stated them</h4>
              <table className="rto__tbl rto__tbl--cmp">
                <thead>
                  <tr>
                    <th>Obligation</th>
                    <th>State</th>
                    <th>Expires</th>
                    <th>Certificate</th>
                    <th>Issued by</th>
                    <th>Raw value</th>
                    <th>From field</th>
                  </tr>
                </thead>
                <tbody>
                  {s.obligations?.map((o) => (
                    <tr key={o.complianceTypeId}>
                      <th scope="row">{o.displayName}</th>
                      <td>{STATUS_LABEL[o.status ?? Status.Unknown] ?? '—'}</td>
                      <td className="mono">{date(o.expiryDate)}</td>
                      <td className="mono">{o.certificateNumber || '—'}</td>
                      <td>{o.providerName || '—'}</td>
                      <td className="mono">{o.rawValue || '—'}</td>
                      {/* Which payload key produced this — the mapping, made visible. */}
                      <td className="mono rto__none">{o.sourceFieldName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/*
              Provenance was a table of six cells a row - label, value, label,
              value, label, value - and at a glance there was no telling which
              was which. Facts about one call are not a table anyway: there are
              no columns, only pairs. A definition list says that outright, and
              each pair now carries its own frame.
            */}
            <section className="rto__wide">
              <h4>Provenance</h4>
              <dl className="rto__facts">
                <div>
                  <dt>Fetched</dt>
                  <dd>{stamp(s.fetchedAtUtc)}</dd>
                </div>
                <div>
                  <dt>Provider</dt>
                  <dd>{s.provider || '—'}</dd>
                </div>
                <div>
                  <dt>HTTP</dt>
                  <dd className="mono">{s.httpStatus ?? '—'}</dd>
                </div>
                <div>
                  <dt>Cost</dt>
                  <dd className="mono">
                    {s.costPaise != null ? `₹${(s.costPaise / 100).toFixed(2)}` : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd className="mono">{s.durationMs != null ? `${s.durationMs} ms` : '—'}</dd>
                </div>
                <div>
                  <dt>Sweep</dt>
                  {/* Null means the per-row button, set means a bulk sweep. One
                      nullable column is why both share this table. */}
                  <dd className="mono">{s.registrySweepId ?? 'per-row sync'}</dd>
                </div>
                <div>
                  <dt>Parsed</dt>
                  <dd>{stamp(s.parsedAtUtc)}</dd>
                </div>
                <div>
                  <dt>Parser</dt>
                  <dd className="mono">v{s.parserVersion}</dd>
                </div>
                <div>
                  <dt>Result</dt>
                  <dd>{PARSE_STATUS[s.parseStatus ?? 1] ?? '—'}</dd>
                </div>
                <div className="rto__facts-wide">
                  {/* The FILE, not its path. The path's first segment is the
                      aggregator's slug, and the vendor's name does not go to a
                      browser - the API returns only the file name now. This is
                      still the exact response: the name carries its timestamp
                      and fetch id. */}
                  <dt>Archived response</dt>
                  <dd className="mono rto__path">{s.responseBlobPath || 'kept inline'}</dd>
                </div>
              </dl>
            </section>

            {/*
              Every key in the response that no mapping claimed.

              Noise on every other screen; the point of this one. A field we paid
              for and never read is invisible without it — which is how a wrong
              mapping survives review.
            */}
            <section className="rto__wide">
              <h4>
                Fields nothing reads
                <span className="rto__count">{unmapped.length}</span>
              </h4>
              {unmapped.length === 0 ? (
                <p className="rto__none">Every field in the response is mapped.</p>
              ) : (
                <table className="rto__tbl rto__tbl--cmp">
                  <thead>
                    <tr>
                      <th>Field in the response</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map(([k, v]) => (
                      <tr key={k}>
                        <th scope="row" className="mono">
                          {k}
                        </th>
                        <td className="mono">
                          {v === null || v === '' ? (
                            <span className="rto__none">empty</span>
                          ) : (
                            String(v)
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="rto__wide">
              <h4>The response itself</h4>
              <button
                type="button"
                className="btn btn--raw"
                onClick={() => setShowRaw((v) => !v)}
                aria-expanded={showRaw}
              >
                {showRaw ? 'Hide' : 'Show'} the archived payload
              </button>

              {showRaw && (
                <pre className="rto__raw mono">
                  {raw.isPending
                    ? 'Loading…'
                    : raw.data
                      ? JSON.stringify(raw.data, null, 2)
                      : 'The archived payload is no longer available.'}
                </pre>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  )
}

/** One field, both sides, with the disagreement marked rather than resolved. */
function Compare({
  label,
  ours,
  theirs,
}: {
  label: string
  ours?: string | null
  theirs?: string | null
}) {
  const differs =
    Boolean(ours) && Boolean(theirs) && ours!.trim().toUpperCase() !== theirs!.trim().toUpperCase()

  return (
    <tr className={differs ? 'rto__differs' : undefined}>
      <th scope="row">{label}</th>
      <td className="mono">{ours || <span className="rto__none">blank</span>}</td>
      <td className="mono">
        {theirs || <span className="rto__none">not stated</span>}
        {differs && <span className="rto__flag">differs</span>}
      </td>
    </tr>
  )
}
